import React, { useEffect, useState, useRef } from 'react';
import { jsPDF } from 'jspdf';
import { useSelector, useDispatch } from 'react-redux';
import { selectLanguage, clearCart } from '../redux/cartSlice';
import { translations } from '../utils/translations';
import { trackPurchase } from '../utils/analytics';
import './ThankYou.css';

const ThankYou = () => {
  const selectedLang = useSelector(selectLanguage) || { code: 'en' };
  const t = translations[selectedLang.code || 'en'] || translations.en;
  const pdfDownloadedRef = useRef(false);
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [order, setOrder] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('session_id') || params.get('order_id')) {
      return null;
    }
    if (window.history.state?.order) {
      return window.history.state.order;
    }
    try {
      const saved = localStorage.getItem('last_completed_order');
      return saved ? JSON.parse(saved) : null;
    } catch (err) {
      console.error('Error loading backup order:', err);
      return null;
    }
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const orderId = params.get('order_id');
    const paymentMethodParam = params.get('method');

    if (sessionId) {
      setLoading(true);
      setError(null);
      fetch(`/api/orders/verify-payment?session_id=${sessionId}`)
        .then(res => {
          if (!res.ok) {
            throw new Error('Failed to verify payment');
          }
          return res.json();
        })
        .then(data => {
          if (data.success && data.order) {
            setOrder(data.order);
            localStorage.setItem('last_completed_order', JSON.stringify(data.order));
            dispatch(clearCart());
            window.history.replaceState({ order: data.order }, '', '/thank-you');
          } else {
            setError(t.checkoutFailedTitle || 'Payment verification failed.');
          }
        })
        .catch(err => {
          console.error('Verification error:', err);
          setError(t.connectionErrorText || 'An error occurred while verifying your payment. Please contact support.');
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (orderId && paymentMethodParam === 'paynl') {
      setLoading(true);
      setError(null);
      fetch(`/api/orders/verify-paynl?order_id=${orderId}`)
        .then(res => {
          if (!res.ok) {
            throw new Error('Failed to verify Pay.nl payment');
          }
          return res.json();
        })
        .then(data => {
          if (data.success && data.order) {
            setOrder(data.order);
            localStorage.setItem('last_completed_order', JSON.stringify(data.order));
            dispatch(clearCart());
            window.history.replaceState({ order: data.order }, '', '/thank-you');
          } else {
            setError('Payment verification with Pay.nl failed or is still pending.');
          }
        })
        .catch(err => {
          console.error('Pay.nl verification error:', err);
          setError('An error occurred while verifying your payment with Pay.nl.');
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (orderId) {
      setLoading(true);
      setError(null);
      fetch(`/api/orders/${orderId}`)
        .then(res => {
          if (!res.ok) {
            throw new Error('Failed to fetch order details');
          }
          return res.json();
        })
        .then(data => {
          if (data && data._id) {
            setOrder(data);
            localStorage.setItem('last_completed_order', JSON.stringify(data));
            dispatch(clearCart());
            window.history.replaceState({ order: data }, '', '/thank-you');
          } else {
            setError('Failed to retrieve order details.');
          }
        })
        .catch(err => {
          console.error('Fetch order error:', err);
          setError('An error occurred while fetching your order details.');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [dispatch, t]);

  useEffect(() => {
    if (order) {
      trackPurchase({
        orderId: order._id,
        transactionId: order._id,
        value: order.totalAmount || order.total,
        currency: 'EUR',
      });
    }
  }, [order]);

  useEffect(() => {
    if (order && !pdfDownloadedRef.current) {
      pdfDownloadedRef.current = true;

      const generatePdf = (imgElement = null) => {
        // Create PDF Invoice
        const doc = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4'
        });

        // Design Color Palette (Smart Telecom Brand colors)
        const brandRed = [190, 30, 45]; // #be1e2d
        const darkSlate = [15, 23, 42]; // #0f172a
        const lightGray = [248, 250, 252]; // #f8fafc
        const textGray = [100, 116, 139]; // #64748b

        // Header top bar background
        doc.setFillColor(...brandRed);
        doc.rect(0, 0, 210, 48, 'F'); // Header height is 48mm

        // Draw logo on top if image is loaded successfully
        if (imgElement) {
          try {
            const logoWidth = 18;
            const logoHeight = (imgElement.naturalHeight / imgElement.naturalWidth) * logoWidth;
            doc.addImage(imgElement, 'PNG', 15, 5, logoWidth, logoHeight);
          } catch (err) {
            console.error('Error drawing logo image on PDF:', err);
          }
        }

        // Header text details
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(20);
        doc.text('Recent Inspiration', 15, 24);

        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'normal');
        doc.text(`${t.callOrWhatsapp}: +31- 687748358  |  smarttelecom.nl@gmail.com`, 15, 30);

        // Legal company details below Call/WhatsApp (removed 'Company: Recent Inspiration')
        doc.text('KVK: 42091099  |  BTW/VAT: NL005488135b34', 15, 35);
        doc.text('Address: Zuylesteinstraat 21, 1333XZ Almere, Netherlands', 15, 40);
        doc.text('Bank: ING BANK N.V.  |  IBAN: NL46 INGB 0119 4676 90  |  BIC: INGBNL2AXXX', 15, 45);

        // Invoice watermark/title
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(t.invoiceReceipt, 140, 16);

        // Customer / Info Block details
        doc.setTextColor(...darkSlate);
        doc.setFontSize(10);
        
        doc.setFont('helvetica', 'bold');
        doc.text(`${t.orderId}:`, 15, 56);
        doc.setFont('helvetica', 'normal');
        doc.text('#' + order._id.substring(order._id.length - 6).toUpperCase(), 35, 56);

        doc.setFont('helvetica', 'bold');
        doc.text(`${t.dateLabel || 'Date'}:`, 15, 62);
        doc.setFont('helvetica', 'normal');
        doc.text(new Date(order.createdAt).toLocaleString(), 35, 62);

        doc.setFont('helvetica', 'bold');
        doc.text(`${t.paymentLabel || 'Payment'}:`, 15, 68);
        doc.setFont('helvetica', 'bold');
        if (order.paymentMethod === 'Bank Transfer') {
          doc.setTextColor(245, 158, 11); // orange status
          doc.text('Pending Bank Transfer', 35, 68);
        } else {
          doc.setTextColor(16, 185, 129); // green status
          doc.text(t.paidConfirmed, 35, 68);
        }
        
        // Reset color
        doc.setTextColor(...darkSlate);

        // Section Headings: Info & Address
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(t.contactInfo, 15, 80);
        doc.text(t.shippingAddress, 110, 80);

        // Table line
        doc.setDrawColor(226, 232, 240);
        doc.line(15, 83, 195, 83);

        // Set small info texts
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...textGray);
        
        doc.text(`${t.fullName}: ${order.fullName}`, 15, 89);
        doc.text(`${t.emailAddress}: ${order.email}`, 15, 95);
        if (order.shippingMethod) {
          doc.text(`${t.shippingMethod || 'Shipping Method'}: ${order.shippingMethod}`, 15, 101);
        }

        // Address listing
        doc.text(order.fullName, 110, 89);
        doc.text(order.address1, 110, 95);
        if (order.address2) {
          doc.text(order.address2, 110, 101);
          doc.text(`${order.city}, ${order.stateProv || ''} ${order.zipCode}`, 110, 107);
          doc.text(order.country, 110, 113);
        } else {
          doc.text(`${order.city}, ${order.stateProv || ''} ${order.zipCode}`, 110, 101);
          doc.text(order.country, 110, 107);
        }

        // Products listing table header
        const tableY = 120;
        doc.setFillColor(...lightGray);
        doc.rect(15, tableY, 180, 8, 'F');

        doc.setTextColor(...darkSlate);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text(t.productDescription, 17, tableY + 5.5);
        doc.text(t.qtyLabel, 120, tableY + 5.5);
        doc.text(t.unitPrice, 145, tableY + 5.5);
        doc.text(t.total, 175, tableY + 5.5);

        // Render product entries loop
        let currentY = tableY + 8;
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...textGray);

        order.items.forEach((item) => {
          const truncatedName = item.name.length > 50 ? item.name.substring(0, 47) + '...' : item.name;
          
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          doc.text(truncatedName, 17, currentY + 5);
          
          // Delivery note
          if (item.deliveryTime) {
            doc.setFontSize(7.5);
            doc.setFont('helvetica', 'italic');
            doc.text(`Delivery: ${item.deliveryTime}`, 17, currentY + 9);
          }
          
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          doc.text(item.quantity.toString(), 122, currentY + 5);
          doc.text(`€${item.price.toFixed(2)}`, 145, currentY + 5);
          doc.text(`€${(item.price * item.quantity).toFixed(2)}`, 175, currentY + 5);

          currentY += item.deliveryTime ? 14 : 10;
          doc.setDrawColor(241, 245, 249);
          doc.line(15, currentY, 195, currentY);
        });

        // Price subtotals summaries layout
        currentY += 5;
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...textGray);
        doc.text(`${t.subtotal}:`, 135, currentY);
        doc.text(`€${order.subtotal.toFixed(2)}`, 175, currentY);

        currentY += 6;
        doc.text(`${t.shipping}:`, 135, currentY);
        doc.text(order.shipping === 0 ? t.free : `€${order.shipping.toFixed(2)}`, 175, currentY);

        currentY += 8;
        doc.setDrawColor(226, 232, 240);
        doc.line(130, currentY - 4, 195, currentY - 4);

        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...brandRed);
        doc.setFontSize(12);
        doc.text(`${t.grandTotal}:`, 135, currentY + 1);
        doc.text(`€${order.total.toFixed(2)}`, 175, currentY + 1);

        // Bottom support text
        doc.setTextColor(...textGray);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.text(t.thankYouShopping, 105, 275, { align: 'center' });

        // Download Invoice PDF file
        doc.save(`receipt_${order._id}.pdf`);
      };

      // Load logo image
      const img = new Image();
      img.onload = () => {
        generatePdf(img);
      };
      img.onerror = () => {
        generatePdf(null);
      };
      img.src = '/images/logo.png';
    }
  }, [order, t]);

  const handleContinueShopping = () => {
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  if (loading) {
    return (
      <div className="thankyou-page-wrapper">
        <div className="thankyou-card">
          <div className="thankyou-loading">
            <div className="thankyou-spinner"></div>
            <h2 style={{ marginTop: '1.5rem', fontWeight: 600 }}>Verifying Payment...</h2>
            <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Please wait while we verify your transaction. Do not refresh or close this page.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="thankyou-page-wrapper">
        <div className="thankyou-card">
          <div className="thankyou-icon error">
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h1 className="thankyou-title">Payment Failed</h1>
          <p className="thankyou-subtitle">{error}</p>
          <button className="thankyou-btn" onClick={handleContinueShopping}>
            {t.goToHomepage}
          </button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="thankyou-page-wrapper">
        <div className="thankyou-card">
          <div className="thankyou-icon error">
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h1 className="thankyou-title">{t.noActiveOrderTitle}</h1>
          <p className="thankyou-subtitle">{t.noActiveOrderDesc}</p>
          <button className="thankyou-btn" onClick={handleContinueShopping}>
            {t.goToHomepage}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="thankyou-page-wrapper">
      <div className="thankyou-card">
        {/* Animated Check icon */}
        <div className="thankyou-icon success">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>

        <h1 className="thankyou-title">{t.thankYou}</h1>
        <p className="thankyou-subtitle">
          {order.paymentMethod === 'Bank Transfer'
            ? 'We will ship your order following confirmation of payment'
            : t.orderSuccessDesc}
        </p>
        <p className="thankyou-subtitle" style={{ marginTop: '8px', fontSize: '0.95rem' }}>
          {order.paymentMethod === 'Bank Transfer' && order.paymentStatus !== 'Paid'
            ? (t.officialInvoiceAfterPayment || 'Your formal invoice will be emailed after we confirm your bank transfer.')
            : (t.officialInvoiceEmail || 'A formal invoice will be emailed to you shortly.')}
        </p>
        
        {/* Order details panel */}
        <div className="thankyou-details-panel">
          <div className="details-row">
            <span className="details-label">{t.orderId}</span>
            <span className="details-val order-id-font">#{order._id.substring(order._id.length - 6).toUpperCase()}</span>
          </div>
          <div className="details-row">
            <span className="details-label">{t.customerName}</span>
            <span className="details-val">{order.fullName}</span>
          </div>
          <div className="details-row">
            <span className="details-label">{t.emailAddress}</span>
            <span className="details-val">{order.email}</span>
          </div>
          {order.shippingMethod && (
            <div className="details-row">
              <span className="details-label">{t.shippingMethod || 'Shipping Method'}</span>
              <span className="details-val">{order.shippingMethod}</span>
            </div>
          )}
          <div className="details-row">
            <span className="details-label">{t.totalAmount}</span>
            <span className="details-val amount-highlight">€{order.total.toFixed(2)}</span>
          </div>
        </div>

        {order.paymentMethod === 'Bank Transfer' && (
          <div 
            className="bank-transfer-info-box" 
            style={{ 
              marginTop: '24px',
              animation: 'fadeIn 0.2s ease-in-out',
              marginBottom: '28px'
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#be1e2d" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="2" ry="2"></rect>
                <line x1="2" y1="10" x2="22" y2="10"></line>
                <line x1="6" y1="15" x2="10" y2="15"></line>
              </svg>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>Bank Account Details</h3>
            </div>
            <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 20px 0', textAlign: 'left' }}>
              Use the following details for direct bank transfers or invoice payments.
            </p>

            {/* Details Card */}
            <div 
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: '20px',
                padding: '24px',
                backgroundColor: '#f8fafc',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                textAlign: 'left'
              }}
            >
              {/* Grid Row 1 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', zIndex: 2 }}>
                <div>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Beneficiary Name</div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>Recent Inspiration</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Bank Name</div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>ING BANK N.V.</div>
                </div>
              </div>

              {/* Divider */}
              <div style={{ borderTop: '1px dashed #e2e8f0', width: '100%', height: 0, zIndex: 2 }} />

              {/* Grid Row 2 (IBAN) */}
              <div style={{ zIndex: 2 }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>IBAN</div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#be1e2d', letterSpacing: '0.5px' }}>NL46 INGB 0119 4676 90</div>
              </div>

              {/* Divider */}
              <div style={{ borderTop: '1px dashed #e2e8f0', width: '100%', height: 0, zIndex: 2 }} />

              {/* Grid Row 3 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '16px', zIndex: 2 }}>
                <div>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>BIC / Swift Code</div>
                  <div style={{ fontSize: '14.5px', fontWeight: '700', color: '#0f172a' }}>INGBNL2AXXX</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Branch</div>
                  <div style={{ fontSize: '13.5px', fontWeight: '700', color: '#0f172a', lineHeight: '1.4' }}>
                    BIJLMERDREEF 106 AMSTERDAM<br />1102CT Netherlands
                  </div>
                </div>
              </div>

              {/* Bank building watermark SVG */}
              <svg 
                width="110" 
                height="110" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="#0f172a" 
                strokeWidth="1" 
                style={{
                  position: 'absolute',
                  right: '15px',
                  bottom: '-10px',
                  opacity: 0.04,
                  pointerEvents: 'none',
                  zIndex: 1
                }}
              >
                <line x1="3" y1="21" x2="21" y2="21"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
                <polygon points="12 2 2 10 22 10 12 2"></polygon>
                <line x1="5" y1="10" x2="5" y2="21"></line>
                <line x1="9" y1="10" x2="9" y2="21"></line>
                <line x1="15" y1="10" x2="15" y2="21"></line>
                <line x1="19" y1="10" x2="19" y2="21"></line>
              </svg>
            </div>

            {/* Notice */}
            <div style={{ borderTop: '1px dashed #e2e8f0', marginTop: '16px', paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center', textAlign: 'left', maxWidth: '500px', margin: '16px auto 0 auto' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#0f172a', textAlign: 'center', width: '100%' }}>
                Payment Reference: <strong style={{ color: '#be1e2d', fontSize: '16px', letterSpacing: '0.5px' }}>#{order._id.substring(order._id.length - 6).toUpperCase()}</strong>
              </p>
              <div style={{ backgroundColor: 'rgba(190, 30, 45, 0.04)', borderLeft: '3px solid #be1e2d', padding: '12px 16px', borderRadius: '4px', fontSize: '13px', lineHeight: '1.6', color: '#334155' }}>
                <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#0f172a' }}>⚠️ Important Instructions:</p>
                <p style={{ margin: '0 0 8px 0' }}>1. Please include your <strong>Order ID (#{order._id.substring(order._id.length - 6).toUpperCase()})</strong> as the payment reference when transferring the order amount.</p>
                <p style={{ margin: 0 }}>2. After completing the payment, kindly send the payment confirmation or transfer receipt to the company's email address (<strong>smarttelecom.nl@gmail.com</strong>) or WhatsApp number (<strong>+31-687748358</strong>) listed below, together with the recipient details, so we can verify your payment and process your order promptly.</p>
              </div>
            </div>
          </div>
        )}

        <p className="receipt-notice-text">
          {t.receiptNotice ? (
            t.receiptNotice.includes("receipt_{id}.pdf") ? (
              t.receiptNotice.split("receipt_{id}.pdf").map((part, idx, arr) => (
                <React.Fragment key={idx}>
                  {part}
                  {idx < arr.length - 1 && <strong style={{ color: '#be1e2d' }}>receipt_{order._id}.pdf</strong>}
                </React.Fragment>
              ))
            ) : (
              t.receiptNotice.split("receipt_{id}").map((part, idx, arr) => (
                <React.Fragment key={idx}>
                  {part}
                  {idx < arr.length - 1 && <strong style={{ color: '#be1e2d' }}>receipt_{order._id}.pdf</strong>}
                </React.Fragment>
              ))
            )
          ) : (
            <>A receipt PDF file <strong style={{ color: '#be1e2d' }}>receipt_{order._id}.pdf</strong> has been automatically generated and downloaded to your downloads folder.</>
          )}
        </p>

        <button className="thankyou-btn" onClick={handleContinueShopping}>
          {t.continueShopping}
        </button>
      </div>
    </div>
  );
};

export default ThankYou;
